import {
  Membership,
  PublicWorkspacePayload,
  SharedGroup,
  SharedGroupSummary,
  Slide,
  WorkspaceConfig,
} from "@shared/types";
import { prepareImageForUpload } from "./image-prep";

export interface SharedGroupRef {
  id: string;
  name: string;
  slides: Slide[];
}

export interface WorkspaceSummary {
  slug: string;
  name: string;
  createdAt: number;
  role: "owner" | "editor";
}

export interface WorkspaceDetail {
  workspace: WorkspaceSummary;
  config: WorkspaceConfig;
  /** Config save counter — echo it back on save to detect concurrent edits. */
  version: number;
  members: Membership[];
  sharedGroups: Record<string, SharedGroupRef>;
}

/** One past save of a workspace config, newest first in the list. */
export interface HistoryEntry {
  version: number;
  savedAt: number;
  /** Admin email, or a label such as "GitHub picture import". */
  savedBy: string;
  slideCount: number;
}

export interface HistoryVersion {
  version: number;
  savedAt: number;
  savedBy: string;
  config: WorkspaceConfig;
}

export interface MediaUploadResult {
  key: string;
  fileName: string;
  contentType: string;
  size: number;
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: init.body && !(init.body instanceof Blob) ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });
  const isJson = res.headers.get("Content-Type")?.includes("application/json");
  const data: any = isJson ? await res.json() : null;
  if (!res.ok) {
    throw new ApiError(res.status, (data && data.error) || `Request failed (${res.status})`);
  }
  return data as T;
}

export const api = {
  register: (email: string, password: string) =>
    request<{ user: { id: string; email: string } }>("/api/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  login: (email: string, password: string) =>
    request<{ user: { id: string; email: string } }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  me: () => request<{ user: { id: string; email: string } }>("/api/auth/me"),

  listWorkspaces: () => request<{ workspaces: WorkspaceSummary[] }>("/api/workspaces"),

  createWorkspace: (name: string, slug?: string) =>
    request<{ workspace: WorkspaceSummary }>("/api/workspaces", {
      method: "POST",
      body: JSON.stringify({ name, slug }),
    }),

  getWorkspace: (slug: string) => request<WorkspaceDetail>(`/api/workspaces/${slug}`),

  renameWorkspace: (slug: string, name: string, newSlug?: string) =>
    request<{ workspace: WorkspaceSummary }>(`/api/workspaces/${slug}`, {
      method: "PATCH",
      body: JSON.stringify({ name, slug: newSlug }),
    }),

  deleteWorkspace: (slug: string) =>
    request<{ ok: true }>(`/api/workspaces/${slug}`, { method: "DELETE" }),

  saveConfig: (slug: string, config: WorkspaceConfig, baseVersion?: number) =>
    request<{ config: WorkspaceConfig; version: number }>(`/api/workspaces/${slug}/config`, {
      method: "PUT",
      body: JSON.stringify({ config, baseVersion }),
    }),

  getHistory: (slug: string) =>
    request<{ entries: HistoryEntry[] }>(`/api/workspaces/${slug}/history`),

  getHistoryVersion: (slug: string, version: number) =>
    request<HistoryVersion>(`/api/workspaces/${slug}/history/${version}`),

  addMember: (slug: string, email: string, role: "owner" | "editor") =>
    request<{ members: Membership[] }>(`/api/workspaces/${slug}/members`, {
      method: "POST",
      body: JSON.stringify({ email, role }),
    }),

  removeMember: (slug: string, email: string) =>
    request<{ members: Membership[] }>(
      `/api/workspaces/${slug}/members/${encodeURIComponent(email)}`,
      { method: "DELETE" },
    ),

  uploadMedia: async (slug: string, original: File) => {
    const file = await prepareImageForUpload(original);
    return request<MediaUploadResult>(`/api/workspaces/${slug}/media`, {
      method: "POST",
      // Encoded: header values can't carry non-Latin-1 characters (ʻokina,
      // curly quotes, macOS screenshot names) — fetch() throws on the raw name.
      headers: {
        "Content-Type": file.type || "application/octet-stream",
        "X-Filename": encodeURIComponent(file.name),
      },
      body: file,
    });
  },

  deleteMedia: (slug: string, key: string) =>
    request<{ ok: true }>(`/api/workspaces/${slug}/media?key=${encodeURIComponent(key)}`, {
      method: "DELETE",
    }),

  // ---- Shared groups ----
  listSharedGroups: () => request<{ groups: SharedGroupSummary[] }>("/api/shared-groups"),

  createSharedGroup: (name: string) =>
    request<{ group: SharedGroup }>("/api/shared-groups", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),

  getSharedGroup: (id: string) => request<{ group: SharedGroup }>(`/api/shared-groups/${id}`),

  saveSharedGroup: (id: string, name: string, slides: Slide[]) =>
    request<{ group: SharedGroup }>(`/api/shared-groups/${id}`, {
      method: "PUT",
      body: JSON.stringify({ name, slides }),
    }),

  deleteSharedGroup: (id: string) =>
    request<{ ok: true }>(`/api/shared-groups/${id}`, { method: "DELETE" }),

  uploadSharedGroupMedia: async (id: string, original: File) => {
    const file = await prepareImageForUpload(original);
    return request<MediaUploadResult>(`/api/shared-groups/${id}/media`, {
      method: "POST",
      headers: {
        "Content-Type": file.type || "application/octet-stream",
        "X-Filename": encodeURIComponent(file.name),
      },
      body: file,
    });
  },

  publicScreensaver: (slug: string) =>
    request<PublicWorkspacePayload>(`/api/public/screensaver/${slug}`),

  // ---- Windows screensaver app distribution ----
  appInfo: () =>
    request<{ available: boolean; size?: number; uploadedAt?: number | null }>("/api/app/screensaver"),

  uploadAppBuild: (file: File) =>
    request<{ available: true; size: number; uploadedAt: number }>("/api/app/screensaver", {
      method: "POST",
      headers: { "Content-Type": "application/octet-stream", "X-Filename": encodeURIComponent(file.name) },
      body: file,
    }),
};

/** Public URL that serves the uploaded Windows screensaver build. */
export const SCREENSAVER_DOWNLOAD_URL = "/app/clinic-screensaver.scr";

export function mediaUrl(key: string): string {
  return `/media/${key.split("/").map(encodeURIComponent).join("/")}`;
}
